/**
 * Fresh-Session QA Contract (PO 2026-08-13).
 *
 * Consumes ``?leviFreshSession=1`` from the URL EXACTLY ONCE at module
 * load — BEFORE any ``useLeviCommand`` hook can mount and hydrate the
 * previous session — so that a controlled real-device iPhone QA can
 * start from a genuinely fresh Levi session.
 *
 * Narrow contract established by the read-only design-boundary
 * forensic:
 *   1. Consume the flag OUTSIDE the useLeviCommand hook lifecycle.
 *   2. When present:
 *      - remove ``jc.levi.session_id`` from localStorage
 *        (so the localStorage hydration branch reads null);
 *      - expose a one-shot signal so the ``/levi/my-current-session``
 *        fallback branch can also skip resurrection on this load;
 *      - strip ``leviFreshSession=1`` from the URL via
 *        history.replaceState so refreshes/SPA nav do not re-consume.
 *   3. Consume exactly once per JS runtime.
 *   4. Let the existing backend ``_load_or_create(None, uid)`` path
 *      mint the fresh session naturally on the first
 *      ``POST /api/levi/command`` submission.
 *
 * Deliberately does NOT:
 *   - modify useLeviCommand lifecycle;
 *   - introduce new mic / VAD / MediaRecorder / TTS / barge-in logic;
 *   - introduce a module-level promise;
 *   - open a new backend endpoint;
 *   - persist any preference (unlike the ``?leviDiag=1`` sticky flag,
 *     this is intentionally one-shot).
 *
 * The Duplicate Levi Voice Owner Lifecycle repair (bdf0072) remains
 * completely untouched.
 */

const SESSION_STORAGE_KEY = "jc.levi.session_id";

// Module-scope one-shot state. Populated at module load, immutable
// thereafter. React StrictMode double-mount is safe because both
// mount cycles read the same finalized module state.
let _wasRequested = false;
let _consumed = false;

function _consumeOnce() {
  if (_consumed) return;
  _consumed = true;
  try {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search || "");
    const raw = params.get("leviFreshSession");
    if (!raw) return;
    const normalized = String(raw).trim().toLowerCase();
    if (!(normalized === "1" || normalized === "true" || normalized === "yes")) {
      return;
    }
    _wasRequested = true;
    // Remove the previous stored session id BEFORE any React hook can
    // read it. The useLeviCommand hydration effect then reads null
    // and the ``/levi/my-current-session`` fallback is guarded by
    // wasFreshSessionRequested() below.
    try {
      if (window.localStorage) {
        window.localStorage.removeItem(SESSION_STORAGE_KEY);
      }
    } catch { /* private mode / disabled storage */ }
    // Strip the flag from the URL so a subsequent refresh or SPA
    // navigation does not re-consume it. Use replaceState so the
    // history entry stays intact and no navigation is triggered.
    try {
      params.delete("leviFreshSession");
      const qs = params.toString();
      const nextSearch = qs ? `?${qs}` : "";
      const nextUrl = `${window.location.pathname}${nextSearch}${window.location.hash || ""}`;
      window.history.replaceState(window.history.state, "", nextUrl);
    } catch { /* history API unavailable — flag stays but consumed */ }
  } catch { /* noop */ }
}

// Consume immediately at module load. This runs BEFORE React starts
// rendering (index.js imports React app which transitively imports
// leviSpeech + this module before any component mounts).
_consumeOnce();

/**
 * True iff the current JS runtime was loaded with
 * ``?leviFreshSession=1``. Used by useLeviCommand's hydration effect
 * to bypass the ``/levi/my-current-session`` fallback that would
 * otherwise resurrect the prior session on this page load.
 */
export function wasFreshSessionRequested() {
  return _wasRequested === true;
}
